import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';
import type { OutcomeRowVM } from './derive';
import { OtherOutcomeCard } from './other-outcome-card';
import { formatResolvedPct } from './other-outcomes-format';
import { FinalShareBar, OutcomeThumb } from './other-outcomes-parts';

/** Shared desktop+mobile grid template so headers and rows align. */
const ROW_GRID = 'grid grid-cols-[1.75rem_minmax(0,1.4fr)_minmax(7rem,1fr)_3rem_4.75rem] items-center gap-x-3';
function OtherOutcomeRow(t0) {
  const $ = _c(40);
  const {
    row,
    rank,
    marketPubkey
  } = t0;
  const {
    formatD6
  } = useFormattedCurrency();
  const t1 = row.isWinner ? "true" : undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(ROW_GRID, "border-b border-border-secondary bg-background-primary px-3 py-2.5 last:border-b-0");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== rank) {
    t3 = <span className="flex items-center justify-center font-mono text-body tabular-nums text-foreground-tertiary">{rank}</span>;
    $[1] = rank;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== marketPubkey || $[4] !== row.imageNsfw || $[5] !== row.imageUrl || $[6] !== row.index || $[7] !== row.isWinner || $[8] !== row.label) {
    t4 = <OutcomeThumb marketPubkey={marketPubkey} index={row.index} label={row.label} imageUrl={row.imageUrl} imageNsfw={row.imageNsfw} size="size-8" showSettledBadge={row.isWinner} />;
    $[3] = marketPubkey;
    $[4] = row.imageNsfw;
    $[5] = row.imageUrl;
    $[6] = row.index;
    $[7] = row.isWinner;
    $[8] = row.label;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== row.label) {
    t5 = <span className="flex min-w-0 items-center gap-1.5"><span className="truncate text-body font-medium text-foreground-primary">{row.label}</span></span>;
    $[10] = row.label;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex min-w-0 items-center gap-2.5">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== row.finalSharePct) {
    t7 = <FinalShareBar pct={row.finalSharePct} />;
    $[15] = row.finalSharePct;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== row.finalSharePct) {
    t8 = formatResolvedPct(row.finalSharePct);
    $[17] = row.finalSharePct;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <span className="w-10 shrink-0 text-right font-mono text-body tabular-nums text-foreground-secondary">{t8}</span>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t9) {
    t10 = <div className="flex items-center gap-2">{t7}{t9}</div>;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== row.peakConsensusPct) {
    t11 = formatResolvedPct(row.peakConsensusPct);
    $[24] = row.peakConsensusPct;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = <span className="text-right font-mono text-body tabular-nums text-foreground-secondary">{t11}</span>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = row.volumeTotalD6 ?? "0";
  let t14;
  if ($[28] !== formatD6 || $[29] !== t13) {
    t14 = formatD6(t13, "compact");
    $[28] = formatD6;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <span className="text-right font-mono text-body tabular-nums text-foreground-secondary">{t14}</span>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t1 || $[34] !== t10 || $[35] !== t12 || $[36] !== t15 || $[37] !== t3 || $[38] !== t6) {
    t16 = <div data-testid="resolved-panel-outcome-row" data-winner={t1} className={t2}>{t3}{t6}{t10}{t12}{t15}</div>;
    $[33] = t1;
    $[34] = t10;
    $[35] = t12;
    $[36] = t15;
    $[37] = t3;
    $[38] = t6;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
interface OutcomesTableProps {
  rows: ReadonlyArray<OutcomeRowVM>;
  marketPubkey: string;
}

/**
 * The outcomes table (Figma 3928-816): a desktop grid (Outcome / Final / Peak /
 * Volume) that collapses to a stacked card per outcome below `md`. The winning
 * outcome is rank 1 (marked with its settled seal); losers follow. Hidden when empty.
 */
export function OtherOutcomesTable(t0) {
  const $ = _c(41);
  const {
    rows,
    marketPubkey
  } = t0;
  const t = useTranslations("market.resolvedPanel");
  if (rows.length === 0) {
    return null;
  }
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(ROW_GRID, "bg-background-tertiary/50 px-3 py-2.5 text-caption text-foreground-tertiary");
    t2 = <span aria-hidden={true} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t) {
    t3 = t("tableOutcome");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span>{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t("tableFinal");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="text-right">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("tablePeak");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <span className="text-right">{t7}</span>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t) {
    t9 = t("tableVolume");
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <span className="text-right">{t9}</span>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t4 || $[20] !== t6 || $[21] !== t8) {
    t11 = <div className={t1}>{t2}{t4}{t6}{t8}{t10}</div>;
    $[18] = t10;
    $[19] = t4;
    $[20] = t6;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== marketPubkey || $[24] !== rows) {
    let t13;
    if ($[26] !== marketPubkey) {
      t13 = (row, i) => <OtherOutcomeRow key={row.index} row={row} rank={i + 1} marketPubkey={marketPubkey} />;
      $[26] = marketPubkey;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    t12 = rows.map(t13);
    $[23] = marketPubkey;
    $[24] = rows;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <div className="hidden md:block">{t11}{t12}</div>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== marketPubkey || $[32] !== rows) {
    let t15;
    if ($[34] !== marketPubkey) {
      t15 = (row_0, i_0) => <OtherOutcomeCard key={row_0.index} row={row_0} rank={i_0 + 1} marketPubkey={marketPubkey} />;
      $[34] = marketPubkey;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    t14 = rows.map(t15);
    $[31] = marketPubkey;
    $[32] = rows;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[36] !== t14) {
    t15 = <div className="flex flex-col md:hidden">{t14}</div>;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t13 || $[39] !== t15) {
    t16 = <div data-testid="resolved-panel-other-outcomes" className="overflow-hidden rounded-[calc(var(--radius-card)-0.25rem)] border pb-2">{t13}{t15}</div>;
    $[38] = t13;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}